/** The safe margin of a cover: text inside it is never cut by a store or a player. A share of the side. */
export const COVER_SAFE_MARGIN = 0.06;
/** How near (a share of the side) a box must be to a line to snap to it. */
export const COVER_SNAP_DISTANCE = 0.012;

export interface SnapBox {
  /** The centre and the size of the box, as shares of the side. */
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SnapResult {
  x: number;
  y: number;
  /** The guide lines to show: x positions of vertical lines and y positions of horizontal lines. */
  vertical: number[];
  horizontal: number[];
}

interface Candidate {
  /** Where the centre of the box goes. */
  centre: number;
  /** Where the guide line shows. */
  line: number;
}

function candidates(size: number, others: readonly number[]): Candidate[] {
  const half = size / 2;
  return [
    // The middle of the cover.
    { centre: 0.5, line: 0.5 },
    // The two sides of the box on the safe margin.
    { centre: COVER_SAFE_MARGIN + half, line: COVER_SAFE_MARGIN },
    { centre: 1 - COVER_SAFE_MARGIN - half, line: 1 - COVER_SAFE_MARGIN },
    // The centres of the other boxes.
    ...others.map((centre) => ({ centre, line: centre })),
  ];
}

function snapAxis(value: number, size: number, others: readonly number[], distance: number): Candidate | null {
  let best: Candidate | null = null;
  for (const candidate of candidates(size, others)) {
    const gap = Math.abs(candidate.centre - value);
    if (gap <= distance && (best === null || gap < Math.abs(best.centre - value))) {
      best = candidate;
    }
  }
  return best;
}

/**
 * Snaps a moved box to the middle of the cover, to the safe margin and to the centres of the other
 * boxes (design cover-editor d4), each axis by itself. Returns the new centre and the guide lines
 * of the snaps. `enabled` false returns the position with no change. Pure.
 */
export function snapBox(
  box: SnapBox,
  others: readonly SnapBox[],
  enabled = true,
  distance = COVER_SNAP_DISTANCE
): SnapResult {
  if (!enabled) {
    return { x: box.x, y: box.y, vertical: [], horizontal: [] };
  }
  const snapX = snapAxis(
    box.x,
    box.width,
    others.map((other) => other.x),
    distance
  );
  const snapY = snapAxis(
    box.y,
    box.height,
    others.map((other) => other.y),
    distance
  );
  return {
    x: snapX?.centre ?? box.x,
    y: snapY?.centre ?? box.y,
    vertical: snapX === null ? [] : [snapX.line],
    horizontal: snapY === null ? [] : [snapY.line],
  };
}

/** A rotation snapped to a quarter turn when it is within `distance` degrees of one. Pure. */
export function snapRotation(degrees: number, distance = 3): number {
  const quarter = Math.round(degrees / 90) * 90;
  return Math.abs(quarter - degrees) <= distance ? quarter : degrees;
}

/** True when a box reaches outside the safe margin (the editor shows the margin then). Pure. */
export function isOutsideSafeMargin(box: SnapBox): boolean {
  return (
    box.x - box.width / 2 < COVER_SAFE_MARGIN - 1e-6 ||
    box.x + box.width / 2 > 1 - COVER_SAFE_MARGIN + 1e-6 ||
    box.y - box.height / 2 < COVER_SAFE_MARGIN - 1e-6 ||
    box.y + box.height / 2 > 1 - COVER_SAFE_MARGIN + 1e-6
  );
}
